
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/contexts/LanguageContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '@/integrations/supabase/client';

interface AnalyticsChartProps {
  period: string;
  type: string;
}

const AnalyticsChart: React.FC<AnalyticsChartProps> = ({ period, type }) => {
  const { t } = useLanguage();

  const { data: chartData, isLoading } = useQuery({
    queryKey: ['analytics-chart', period],
    queryFn: async () => {
      const now = new Date();
      const periodDays = parseInt(period);
      const startDate = new Date(now.getTime() - periodDays * 24 * 60 * 60 * 1000);

      // Generate date range
      const dates = [];
      for (let i = 0; i < periodDays; i++) {
        const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
        dates.push(date.toISOString().split('T')[0]);
      }

      // For now, return mock data - in real implementation, query daily_metrics table
      const mockData = dates.map(date => ({
        date,
        newListings: Math.floor(Math.random() * 20) + 5,
        chats: Math.floor(Math.random() * 50) + 20,
        visits: Math.floor(Math.random() * 30) + 10,
        bookings: Math.floor(Math.random() * 15) + 3,
        verifiedListings: Math.floor(Math.random() * 10) + 2
      }));

      return mockData;
    }
  });

  const renderChart = (data: any[], dataKey: string) => {
    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (type) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey={dataKey} stroke="#8884d8" strokeWidth={2} />
          </LineChart>
        );
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Bar dataKey={dataKey} fill="#8884d8" />
          </BarChart>
        );
      default:
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Area type="monotone" dataKey={dataKey} stroke="#8884d8" fill="#8884d8" />
          </AreaChart>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Tabs defaultValue="listings" className="w-full">
      <TabsList className="grid w-full grid-cols-5">
        <TabsTrigger value="listings">{t('listings', 'القوائم', 'Listings')}</TabsTrigger>
        <TabsTrigger value="chats">{t('chats', 'المحادثات', 'Chats')}</TabsTrigger>
        <TabsTrigger value="visits">{t('visits', 'الزيارات', 'Visits')}</TabsTrigger>
        <TabsTrigger value="bookings">{t('bookings', 'الحجوزات', 'Bookings')}</TabsTrigger>
        <TabsTrigger value="verified">{t('verified', 'متحقق', 'Verified')}</TabsTrigger>
      </TabsList>
      
      <TabsContent value="listings" className="mt-6">
        <ResponsiveContainer width="100%" height={350}>
          {renderChart(chartData || [], 'newListings')}
        </ResponsiveContainer>
      </TabsContent>
      
      <TabsContent value="chats" className="mt-6">
        <ResponsiveContainer width="100%" height={350}>
          {renderChart(chartData || [], 'chats')}
        </ResponsiveContainer>
      </TabsContent>
      
      <TabsContent value="visits" className="mt-6">
        <ResponsiveContainer width="100%" height={350}>
          {renderChart(chartData || [], 'visits')}
        </ResponsiveContainer>
      </TabsContent>
      
      <TabsContent value="bookings" className="mt-6">
        <ResponsiveContainer width="100%" height={350}>
          {renderChart(chartData || [], 'bookings')}
        </ResponsiveContainer>
      </TabsContent>
      
      <TabsContent value="verified" className="mt-6">
        <ResponsiveContainer width="100%" height={350}>
          {renderChart(chartData || [], 'verifiedListings')}
        </ResponsiveContainer>
      </TabsContent>
    </Tabs>
  );
};

export default AnalyticsChart;
